import { DEFAULT_API } from './apiDefaults.js';
import { ApiError } from '../errors/ApiError.js';
import { ErrorCode } from '../../core/errors/ErrorCodes.js';

// The one place backend infrastructure is configured (Milestone 7.3).
//
// The API contract is explicitly marked PROPOSED: base URL, auth scheme and
// exact routes are placeholders. Nothing here is hardcoded to the documented
// example host - `baseUrl` is required from the integrator, and the example
// domain appears nowhere in this file. That is deliberate, not incidental: it
// is what lets the same build point at a mock, a staging backend, or
// production without a code change.
//
// Validated and frozen once at construction, matching the SDK-wide
// configuration invariant (ADR-025): nothing re-validates during playback and
// nothing may mutate this afterwards.
export class ApiConfiguration {
  constructor(options = {}) {
    const {
      baseUrl,
      publisherId = null,
      apiKey = null,
      timeout = DEFAULT_API.timeoutMs,
      auth = {},
      telemetry = {},
      logging = {},
    } = options;

    requireNonEmptyString(baseUrl, 'baseUrl');
    // The site key (the dashboard's per-site API key). It is public - it ships
    // in the page - and it is the one thing every Playbid endpoint resolves the
    // publisher from.
    requireNonEmptyString(apiKey, 'apiKey');
    if (publisherId !== undefined && publisherId !== null) {
      requireNonEmptyString(publisherId, 'publisherId');
    }

    // Trailing slashes are normalized away so path joining stays a single
    // concatenation everywhere downstream instead of a per-call guard.
    this.baseUrl = String(baseUrl).replace(/\/+$/, '');
    this.apiKey = apiKey;
    // Echoed on every telemetry body for correlation only; the backend always
    // takes the publisher from the verified bearer. It is the site key unless
    // an integrator names it explicitly.
    this.publisherId = publisherId ?? apiKey;
    this.timeout = requirePositiveNumber(timeout, 'timeout');

    this.auth = Object.freeze({
      enabled: auth.enabled ?? DEFAULT_API.auth.enabled,
      // Refresh this many ms before the token actually expires, so a request
      // is never sent with a token that dies in flight.
      refreshSkewMs: requirePositiveNumber(
        auth.refreshSkewMs ?? DEFAULT_API.auth.refreshSkewMs,
        'auth.refreshSkewMs',
      ),
    });

    this.telemetry = Object.freeze({
      enabled: telemetry.enabled ?? DEFAULT_API.telemetry.enabled,
      batchSize: requirePositiveNumber(
        telemetry.batchSize ?? DEFAULT_API.telemetry.batchSize,
        'telemetry.batchSize',
      ),
      flushIntervalMs: requirePositiveNumber(
        telemetry.flushIntervalMs ?? DEFAULT_API.telemetry.flushIntervalMs,
        'telemetry.flushIntervalMs',
      ),
      maxQueueSize: requirePositiveNumber(
        telemetry.maxQueueSize ?? DEFAULT_API.telemetry.maxQueueSize,
        'telemetry.maxQueueSize',
      ),
      heartbeatIntervalMs: requirePositiveNumber(
        telemetry.heartbeatIntervalMs ?? DEFAULT_API.telemetry.heartbeatIntervalMs,
        'telemetry.heartbeatIntervalMs',
      ),
      adEvents: telemetry.adEvents ?? DEFAULT_API.telemetry.adEvents,
    });

    this.logging = Object.freeze({
      enabled: logging.enabled ?? DEFAULT_API.logging.enabled,
      logger: logging.logger ?? null,
    });

    Object.freeze(this);
  }
}

function isNonEmptyString(value) {
  return typeof value === 'string' && value.trim().length > 0;
}

function requireNonEmptyString(value, label) {
  if (!isNonEmptyString(value)) {
    throw new ApiError(`ApiConfiguration: ${label} is required and must be a non-empty string`, {
      code: ErrorCode.SDK_API_NOT_CONFIGURED,
      context: { field: label },
    });
  }
}

function requirePositiveNumber(value, label) {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
    throw new ApiError(`ApiConfiguration: ${label} must be a positive number`, {
      code: ErrorCode.SDK_API_NOT_CONFIGURED,
      context: { field: label },
    });
  }
  return value;
}
